// https://github.com/ChainAgnostic/CAIPs/blob/master/CAIPs/caip-19.md
import { fromChainId, toChainId } from '../chainId/chainId';
import { VALID_ASSET_NAMESPACE } from '../constants';
import { assertIsAssetNamespace, assertIsChainNamespace, assertIsChainReference, assertValidChainPartsPair, isAssetIdParts, isAssetNamespace, } from '../typeGuards';
/**
 * validate that a value is a string slip44 value
 * @see https://github.com/satoshilabs/slips/blob/master/slip-0044.md
 * @param {string} value - possible slip44 value
 */
const isValidSlip44 = (value) => {
    // TCY is a weird AssetId, its asset ref part is not a number
    if (value === 'tcy')
        return true;
    const n = Number(value);
    // slip44 has a max value of an unsigned 32-bit integer
    return !isNaN(n) && n >= 0 && n < 4294967296;
};
const isToAssetIdWithChainIdArgs = (args) => !!args.chainId;
export const toAssetId = (args) => {
    const { assetNamespace, assetReference } = args;
    assertIsAssetNamespace(assetNamespace);
    if (!assetReference)
        throw new Error('toAssetId: No assetReference provided');
    const { chainId, chainNamespace, chainReference } = (() => {
        if (isToAssetIdWithChainIdArgs(args)) {
            const fromChainIdResult = fromChainId(args.chainId);
            return {
                chainId: args.chainId,
                chainNamespace: fromChainIdResult.chainNamespace,
                chainReference: fromChainIdResult.chainReference,
            };
        }
        else
            return {
                chainId: toChainId({
                    chainNamespace: args.chainNamespace,
                    chainReference: args.chainReference,
                }),
                chainNamespace: args.chainNamespace,
                chainReference: args.chainReference,
            };
    })();
    assertIsChainNamespace(chainNamespace);
    assertIsChainReference(chainReference);
    assertValidChainPartsPair(chainNamespace, chainReference);
    if (!VALID_ASSET_NAMESPACE[chainNamespace].includes(assetNamespace) ||
        !isAssetNamespace(assetNamespace))
        throw new Error(`toAssetId: AssetNamespace ${assetNamespace} not supported for Chain Namespace ${chainNamespace}`);
    if (assetNamespace === 'slip44' && !isValidSlip44(String(assetReference))) {
        throw new Error(`Invalid reference for namespace slip44`);
    }
    const assetReferenceNormalized = (() => {
        const assertContractAddress = (address) => {
            if (!address.startsWith('0x'))
                throw new Error(`toAssetId: assetReference must start with 0x: ${assetReference}`);
            if (address.length !== 42)
                throw new Error(`toAssetId: assetReference length must be 42, length: ${assetReference.length}, ${assetReference}`);
        };
        switch (assetNamespace) {
            case 'erc20':
            case 'bep20':
                assertContractAddress(assetReference);
                return assetReference.toLowerCase();
            case 'erc721':
            case 'erc1155':
            case 'bep721':
            case 'bep1155':
                // caip-22 (https://github.com/ChainAgnostic/CAIPs/blob/master/CAIPs/caip-22.md)
                // caip-29 (https://github.com/ChainAgnostic/CAIPs/blob/master/CAIPs/caip-29.md)
                const [address] = assetReference.split('/');
                assertContractAddress(address);
                return assetReference.toLowerCase();
            default:
                return assetReference;
        }
    })();
    return `${chainId}/${assetNamespace}:${assetReferenceNormalized}`;
};
// NOTE: perf critical - benchmark any changes
export const fromAssetId = (assetId) => {
    const slashIdx = assetId.indexOf('/');
    const chainId = assetId.substring(0, slashIdx);
    const assetParts = assetId.substring(slashIdx + 1);
    const { chainNamespace, chainReference } = fromChainId(chainId);
    const idx = assetParts.indexOf(':');
    const assetNamespace = assetParts.substring(0, idx);
    const assetReference = assetParts.substring(idx + 1);
    if (!isAssetIdParts(chainNamespace, chainReference, assetNamespace))
        throw new Error(`fromAssetId: invalid AssetId: ${assetId}`);
    const assetReferenceNormalized = (() => {
        switch (assetNamespace) {
            case 'erc20':
            case 'bep20':
            case 'erc721':
            case 'erc1155':
            case 'bep721':
            case 'bep1155':
                return assetReference.toLowerCase();
            default:
                return assetReference;
        }
    })();
    return {
        chainId,
        chainNamespace: chainNamespace,
        chainReference: chainReference,
        assetNamespace: assetNamespace,
        assetReference: assetReferenceNormalized,
    };
};
// NOTE: perf critical - benchmark any changes
export const isNft = (assetId) => {
    const slashIdx = assetId.indexOf('/');
    const assetParts = assetId.substring(slashIdx + 1);
    const idx = assetParts.indexOf(':');
    const assetNamespace = assetParts.substring(0, idx);
    return ['erc721', 'erc1155', 'bep721', 'bep1155'].includes(assetNamespace);
};
export const toCAIP19 = toAssetId;
export const fromCAIP19 = fromAssetId;
